import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Headphones, Phone, Lock, Building2, Globe, User, ArrowRight, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import NewUserWalkthrough from '@/components/NewUserWalkthrough';

// Local storage key for remembered credentials
const SIP_CREDS_KEY = 'connect_sip_creds';

// Simple obfuscation (not encryption, just to avoid plain text storage)
const obfuscate = (str) => btoa(str.split('').reverse().join(''));
const deobfuscate = (str) => {
  try {
    return atob(str).split('').reverse().join('');
  } catch {
    return '';
  }
};

const getSavedCredentials = () => {
  try {
    const saved = localStorage.getItem(SIP_CREDS_KEY);
    if (!saved) return null;
    const creds = JSON.parse(saved);
    return {
      phone: creds.phone,
      password: deobfuscate(creds.password)
    };
  } catch {
    return null;
  }
};

const saveCredentials = (phone, password) => {
  localStorage.setItem(SIP_CREDS_KEY, JSON.stringify({
    phone,
    password: obfuscate(password)
  }));
};

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, firstLogin, isAuthenticated } = useAuth();
  
  // Check URL param for initial tab
  const initialTab = searchParams.get('tab') === 'register' ? 'register' : 'login';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  
  // Login form data
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberCredentials, setRememberCredentials] = useState(false);
  
  // Register form data
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regAccountNumber, setRegAccountNumber] = useState('');
  const [regSipDomain, setRegSipDomain] = useState('');
  const [rememberRegCredentials, setRememberRegCredentials] = useState(false);
  
  // New user walkthrough
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const loginInProgressRef = useRef(false);

  // Set page title
  useEffect(() => {
    document.title = "Connect | Cloud PBX Console";
  }, []);

  // Load saved credentials on mount
  useEffect(() => {
    const saved = getSavedCredentials();
    if (saved?.phone && saved?.password) {
      setLoginPhone(saved.phone);
      setLoginPassword(saved.password);
      setRememberCredentials(true);
    }
  }, []);

  // Redirect if already authenticated (but not if walkthrough is showing or login in progress)
  useEffect(() => {
    if (isAuthenticated && !showWalkthrough && !loginInProgressRef.current) {
      navigate('/app');
    }
  }, [isAuthenticated, navigate, showWalkthrough]);

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginPhone.trim() || !loginPassword.trim()) {
      toast.error('Please enter your phone number and password');
      return;
    }

    setLoading(true);
    loginInProgressRef.current = true;
    try {
      const data = await login(loginPhone, loginPassword);
      
      // Save credentials if requested
      if (rememberCredentials) {
        saveCredentials(loginPhone, loginPassword);
      }
      
      if (data?.user?.is_first_login) {
        setNewUserName(data.user.name || '');
        setShowWalkthrough(true);
        loginInProgressRef.current = false;
        toast.success('Welcome! Let us get you set up.');
      } else {
        loginInProgressRef.current = false;
        toast.success('Welcome back!');
        navigate('/app');
      }
    } catch (error) {
      loginInProgressRef.current = false;
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle registration
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!regPhone.trim() || !regPassword.trim() || !regAccountNumber.trim() || !regSipDomain.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!regName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    // Client-side phone number validation
    const phone = regPhone.trim();
    const account = regAccountNumber.trim();
    
    if (!/^\d+$/.test(phone)) {
      toast.error('Phone number must contain only digits');
      return;
    }
    
    if (!phone.startsWith(account)) {
      toast.error(`Phone number must start with your account number (${account})`);
      return;
    }
    
    const expectedLength = account.length + 4;
    if (phone.length !== expectedLength) {
      toast.error(`Phone number should be ${expectedLength} digits (account number + 4-digit extension). You entered ${phone.length} digits.`);
      return;
    }

    setLoading(true);
    try {
      await firstLogin(regPhone, regPassword, regSipDomain, regAccountNumber, regName);
      
      // Save credentials if requested
      if (rememberRegCredentials) {
        saveCredentials(regPhone, regPassword);
      }
      
      toast.success('Account created successfully! Welcome!');
      // Show walkthrough for new users
      setNewUserName(regName);
      setShowWalkthrough(true);
    } catch (error) {
      // Check if this is a user limit exceeded error for free plan
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail?.error_code === 'USER_LIMIT_EXCEEDED') {
        if (errorDetail.upgrade_required) {
          toast.error('User limit reached. Redirecting to upgrade page...');
          setTimeout(() => {
            navigate('/subscribe?reason=user_limit');
          }, 1500);
        } else {
          toast.error(errorDetail.message || 'User limit reached. Please contact your administrator.');
        }
      } else {
        toast.error(error.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWalkthroughClose = () => {
    setShowWalkthrough(false);
    navigate('/app');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="login-page">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-background to-background" />
      
      {/* New User Walkthrough Modal */}
      <NewUserWalkthrough 
        open={showWalkthrough} 
        onClose={handleWalkthroughClose}
        userName={newUserName}
      />
      
      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/connect-logo.png" alt="CONNECT" className="w-16 h-16 rounded-2xl mx-auto mb-4" />
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
            CONNECT
          </h1>
          <p className="text-muted-foreground mt-1">Cloud PBX Console</p>
        </div>

        <Card className="border-border/50 bg-card/50 backdrop-blur-sm" data-testid="auth-card">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 m-0 rounded-b-none">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login" className="mt-0">
              <CardHeader className="text-center pb-2">
                <CardTitle>Welcome Back</CardTitle>
                <CardDescription>For returning users in a subscribed company</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-phone">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-phone"
                        type="text"
                        placeholder="e.g., 830200010005"
                        value={loginPhone}
                        onChange={(e) => setLoginPhone(e.target.value)}
                        className="pl-10"
                        data-testid="login-phone-input"
                        autoFocus
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Your SIP username from your Cloud PBX</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="Your SIP password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="pl-10"
                        data-testid="login-password-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember-credentials-login"
                      checked={rememberCredentials}
                      onCheckedChange={setRememberCredentials}
                      data-testid="remember-credentials-login"
                    />
                    <Label 
                      htmlFor="remember-credentials-login" 
                      className="text-sm font-normal cursor-pointer flex items-center gap-1.5"
                    >
                      <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                      Remember me on this device
                    </Label>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-btn">
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4 mr-2" />
                    )}
                    Sign In
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    Don't have an account?{' '}
                    <button 
                      onClick={() => setActiveTab('register')}
                      className="text-primary hover:underline font-medium"
                    >
                      Register here
                    </button>
                  </p>
                </div>
              </CardContent>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register" className="mt-0">
              <CardHeader className="text-center pb-2">
                <CardTitle>Create Account</CardTitle>
                <CardDescription>For first-time users in a subscribed company</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-name">Your Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="reg-name"
                        type="text"
                        placeholder="John Smith"
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        className="pl-10"
                        data-testid="reg-name-input"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-phone">Phone Number (SIP Username) *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="reg-phone"
                        type="text"
                        placeholder={regAccountNumber ? `${regAccountNumber}XXXX` : "e.g., 830200010005"}
                        value={regPhone}
                        onChange={(e) => setRegPhone(e.target.value)}
                        className="pl-10"
                        data-testid="reg-phone-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {regAccountNumber 
                        ? `Format: ${regAccountNumber} + 4-digit extension (${regAccountNumber.length + 4} digits total)`
                        : "Your SIP username from your PBX provider"}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-password">SIP Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="reg-password"
                        type="password"
                        placeholder="Your SIP password"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        className="pl-10"
                        data-testid="reg-password-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">The password assigned by your PBX administrator</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-account">Account Number *</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="reg-account"
                        type="text"
                        placeholder="e.g., 83020001"
                        value={regAccountNumber}
                        onChange={(e) => setRegAccountNumber(e.target.value)}
                        className="pl-10"
                        data-testid="reg-account-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Your company's Cloud PBX account number</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-domain">SIP Domain *</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="reg-domain"
                        type="text"
                        placeholder="e.g., securevoip.nz"
                        value={regSipDomain}
                        onChange={(e) => setRegSipDomain(e.target.value)}
                        className="pl-10"
                        data-testid="reg-domain-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Your SIP provider domain (ask your PBX administrator)</p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember-credentials-register"
                      checked={rememberRegCredentials}
                      onCheckedChange={setRememberRegCredentials}
                      data-testid="remember-credentials-register"
                    />
                    <Label 
                      htmlFor="remember-credentials-register" 
                      className="text-sm font-normal cursor-pointer flex items-center gap-1.5"
                    >
                      <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                      Remember me on this device
                    </Label>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading} data-testid="register-btn">
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4 mr-2" />
                    )}
                    Create Account
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{' '}
                    <button 
                      onClick={() => setActiveTab('login')}
                      className="text-primary hover:underline font-medium"
                    >
                      Sign in here
                    </button>
                  </p>
                </div>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Powered by CONNECT
        </p>
      </div>
    </div>
  );
}
